import { Alert, Select } from '@mantine/core';
import {
  IconCircleCheck,
  IconHourglass,
  IconInfoCircle,
  IconListDetails,
  IconSend,
  IconShip,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { TablePager } from '../purchasing/table.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { LoadFailed, RowsSkeleton, usePmsQuery } from './PmsWorkParts.js';
import styles from './SetupChanges.module.css';
import type { PmsSource } from './source.js';
import {
  type SetupChangeItem,
  type SetupChangeListView,
  type SetupChangeStatus,
  SETUP_CHANGE_STATUSES as STATUSES,
  SETUP_CHANGE_TABLES as TABLES,
} from './types.js';

export { SETUP_CHANGES_PATH, setupChangesPath } from './types.js';

export interface SetupChangesScreenProps {
  source: PmsSource;
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; vesselId: string; list: SetupChangeListView };

const ALL = 'all';

const STATUS_TONE: Record<SetupChangeStatus, StatusTone> = {
  pending: 'warning',
  applied: 'success',
  rejected: 'danger',
};

/**
 * Fleet ▸ Vessels ▸ Setup Changes (`/fleet/setup-changes?vessel=`; ADR 0142 §3): kurulumdaki
 * etkin gemide ofisin PMS değişiklikleri (ekipman, bakım planı) ve gemiden dönen sonuçları:
 * durum (Pending / Applied / Rejected), modül, kayıt, yapan, zaman ve ret gerekçesi. Özet
 * kartları durum süzgeci kurar; süzgeç ve sayfalama sunucuda, seçimler adreste kalır
 * (`?vessel=&status=&table=&page=`). Yalnız ofiste; liste modülü görebilen kişiye görünür.
 */
export function SetupChangesScreen({ source }: SetupChangesScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.setupChangeList.${key}`, values);
  const [query, setQuery] = usePmsQuery();
  const [vessels, setVessels] = useState<PersonnelVessel[] | null>(null);
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);

  const requested = query.get('vessel');
  const vesselId = vessels?.find((v) => v.id === requested)?.id ?? vessels?.[0]?.id ?? null;
  const status = STATUSES.find((s) => s === query.get('status')) ?? null;
  const table = TABLES.find((s) => s === query.get('table')) ?? null;
  const pageParam = Number(query.get('page') ?? '1');
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  useEffect(() => {
    let live = true;
    source
      .vessels()
      .then((list) => {
        if (live) setVessels(list);
      })
      .catch(() => {
        if (live) setVessels([]);
      });
    return () => {
      live = false;
    };
  }, [source]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yeniden okutur
  useEffect(() => {
    if (!source.setupChanges || vesselId === null) return;
    let live = true;
    setData((current) =>
      current.kind === 'ready' && current.vesselId === vesselId ? current : { kind: 'loading' },
    );
    source
      .setupChanges(vesselId, { status, table, page })
      .then((list) => {
        if (live) setData({ kind: 'ready', vesselId, list });
      })
      .catch((error: unknown) => {
        if (live) {
          const code = (error as { code?: unknown } | null)?.code;
          setData({ kind: 'error', code: typeof code === 'string' ? code : 'GENERIC' });
        }
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, status, table, page, reloads]);

  const list = data.kind === 'ready' && data.vesselId === vesselId ? data.list : null;
  const update = (patch: Record<string, string | null>) => setQuery({ page: null, ...patch });
  const counts = list?.counts;
  const all = counts ? counts.pending + counts.applied + counts.rejected : 0;
  const pages = list ? Math.max(1, Math.ceil(list.total / list.pageSize)) : 1;

  const tile = (
    key: SetupChangeStatus | null,
    icon: ReactNode,
    value: number,
    tone: 'default' | 'warning' | 'success' | 'danger',
  ) => (
    <button
      key={key ?? ALL}
      type="button"
      className={styles.tileButton}
      aria-pressed={key !== null && status === key}
      onClick={() => update({ status: key === null || status === key ? null : key })}
    >
      <StatTile
        icon={icon}
        label={k(`kpi.${key ?? 'all'}`)}
        value={counts ? value : '—'}
        tone={tone}
        wrapLabel
      />
    </button>
  );

  const header = (
    <PageHeader
      title={k('title')}
      description={k('description')}
      actions={
        vessels && vessels.length > 0 ? (
          <span className={work.headerActions}>
            <Select
              aria-label={k('vessel')}
              className={classes.vesselPicker}
              size="sm"
              leftSection={<IconShip size={16} />}
              data={vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={vesselId}
              allowDeselect={false}
              onChange={(id) => update({ vessel: id, status: null, table: null })}
            />
          </span>
        ) : undefined
      }
    />
  );

  if (vessels !== null && vessels.length === 0) {
    return (
      <div className={classes.screen} data-setup-changes-screen>
        {header}
        <Panel title={k('listTitle')}>
          <EmptyState
            icon={<IconShip size={22} />}
            title={k('noVessel')}
            description={k('noVesselHint')}
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className={classes.screen} data-setup-changes-screen>
      {header}
      {list && !list.vessel.activated ? (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />} role="status">
          {k('notActivated')}
        </Alert>
      ) : null}
      {list && list.vessel.setupState === 'live' ? (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />} role="status">
          {k('live')}
        </Alert>
      ) : null}
      <div className={styles.tiles}>
        {tile(null, <IconListDetails size={20} />, all, 'default')}
        {tile(
          'pending',
          <IconHourglass size={20} />,
          counts?.pending ?? 0,
          (counts?.pending ?? 0) > 0 ? 'warning' : 'default',
        )}
        {tile('applied', <IconCircleCheck size={20} />, counts?.applied ?? 0, 'success')}
        {tile(
          'rejected',
          <IconX size={20} />,
          counts?.rejected ?? 0,
          (counts?.rejected ?? 0) > 0 ? 'danger' : 'default',
        )}
      </div>
      <Panel
        title={k('listTitle')}
        {...(list
          ? {
              extra: (
                <StatusBadge tone={list.vessel.setupState === 'live' ? 'success' : 'info'}>
                  {k(`stage.${list.vessel.setupState}`)}
                </StatusBadge>
              ),
            }
          : {})}
      >
        <div className={styles.toolbar}>
          <Select
            aria-label={k('columns.status')}
            placeholder={t('pms.postponements.allStatuses')}
            clearable
            clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
            data={STATUSES.map((s) => ({ value: s, label: k(`statuses.${s}`) }))}
            value={status}
            onChange={(value) => update({ status: value })}
          />
          {list && list.tables.length > 1 ? (
            <Select
              aria-label={k('module')}
              placeholder={k('allModules')}
              clearable
              clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
              data={list.tables.map((m) => ({ value: m, label: k(`modules.${m}`) }))}
              value={table}
              onChange={(value) => update({ table: value })}
            />
          ) : null}
        </div>
        {data.kind === 'error' ? (
          <LoadFailed code={data.code} onRetry={() => setReloads((n) => n + 1)} />
        ) : list === null ? (
          <RowsSkeleton />
        ) : all === 0 ? (
          <EmptyState
            icon={<IconSend size={22} />}
            title={k('empty')}
            description={k('emptyHint')}
          />
        ) : list.items.length === 0 ? (
          <EmptyState title={k('noMatch')} />
        ) : (
          <>
            <section
              className={classes.tableBox}
              aria-label={t('pms.equipment.tableOf', { name: k('listTitle') })}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={classes.table} data-cards>
                <thead>
                  <tr>
                    {(
                      [
                        'status',
                        'module',
                        'change',
                        'record',
                        'requestedBy',
                        'requestedAt',
                        'result',
                      ] as const
                    ).map((column) => (
                      <th key={column} scope="col">
                        {k(`columns.${column}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((item) => (
                    <SetupChangeRow key={item.id} item={item} />
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={list.page}
              pages={pages}
              summary={t('pms.work.showing', {
                from: (list.page - 1) * list.pageSize + 1,
                to: (list.page - 1) * list.pageSize + list.items.length,
                total: list.total,
              })}
              label={t('pms.work.pages', { name: k('listTitle') })}
              onPage={(next) => setQuery({ page: next === 1 ? null : String(next) })}
            />
          </>
        )}
      </Panel>
    </div>
  );
}

export function SetupChangeRow({ item }: { item: SetupChangeItem }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.setupChangeList.${key}`, values);
  const record = [item.code, item.name].filter(Boolean).join(' · ') || '—';
  return (
    <tr data-status={item.status}>
      <td data-label={k('columns.status')}>
        <StatusBadge tone={STATUS_TONE[item.status]}>{k(`statuses.${item.status}`)}</StatusBadge>
      </td>
      <td data-label={k('columns.module')}>{k(`modules.${item.targetTable}`)}</td>
      <td data-label={k('columns.change')}>{k(`ops.${item.op}`)}</td>
      <td data-label={k('columns.record')} className={work.wrapCell}>
        <span className={styles.stack}>
          <span>{record}</span>
          {item.requestReason ? (
            <span className={work.note}>{k('removeReason', { reason: item.requestReason })}</span>
          ) : null}
        </span>
      </td>
      <td data-label={k('columns.requestedBy')}>{item.createdByName ?? '—'}</td>
      <td data-label={k('columns.requestedAt')}>{formatDateTime(item.createdAt)}</td>
      <td data-label={k('columns.result')} className={work.wrapCell}>
        <SetupChangeResult item={item} />
      </td>
    </tr>
  );
}

/** Sonuç: bekleyende sonraki senkron, uygulananda zaman, reddedilende gerekçe ve kodu. */
function SetupChangeResult({ item }: { item: SetupChangeItem }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.setupChangeList.${key}`, values);
  if (item.status === 'pending') return <span className={work.note}>{k('waiting')}</span>;
  const at = formatDateTime(item.resultAt);
  if (item.status === 'applied') return <span>{k('appliedAt', { at })}</span>;
  const text = item.reasonCode
    ? t(`pms.setupChangeList.reasons.${item.reasonCode}`, {
        defaultValue: t(`pms.errors.${item.reasonCode}`, {
          defaultValue: item.reason ?? item.reasonCode,
        }),
      })
    : (item.reason ?? '—');
  return (
    <span className={styles.stack}>
      <span className={styles.rejected}>{text}</span>
      <span className={work.note}>
        {k('rejectedAt', { at })}
        {item.reasonCode ? <code className={styles.code}>{item.reasonCode}</code> : null}
      </span>
    </span>
  );
}
